/**
 * The `infra.error.unhandled` boundary — installed as a side effect of
 * importing this module, at module scope, so it exists before the rest of the
 * app's module graph has evaluated.
 *
 * WHY THIS IS NOT AN EFFECT, AND NOT A CALL IN main.tsx's BODY
 *
 * The failures this boundary exists to capture are the blank-page class: a
 * throw while a module is evaluating, a throw during the root render
 * (main.tsx's own `missing #root element` is the one this template ships), and
 * a throw in the first `App` render. All three happen before React commits its
 * first pass, so listeners attached in a `useEffect` never see them — which
 * means a boundary written that way reports nothing at all for the most
 * damaging failure mode the whole pipeline exists to capture.
 *
 * Moving the listeners into main.tsx's own body is not enough either: ES
 * imports are hoisted and every imported module evaluates before the first
 * statement of the importing module runs, so a module that throws while
 * evaluating would still do it before a listener written there could exist.
 * The listener has to be a side effect of a module that is itself the *first*
 * import of main.tsx — which is exactly what this module is, and why main.tsx
 * imports it for its side effect only, out of alphabetical order, with a
 * comment saying so. The same "this must be in place before React is up"
 * reasoning already governs `watchInstallability()` at the top of main.tsx.
 *
 * WHY THE IMPORTS HERE ARE DEEP, AND FEW
 *
 * Everything this module imports evaluates *before* it, so its own import
 * graph is the one window the boundary cannot cover. That is the whole reason
 * it reaches past @bindery/logging's barrel (a supported entry — see that
 * package's `"./*"` export) for the pieces it needs: the barrel also exports
 * `WebVitals`, which pulls in React and the `web-vitals` library, and under
 * `vite dev` (unbundled ESM, no tree-shaking) that whole graph would evaluate
 * ahead of the listener for no benefit.
 *
 * For the same reason this module must never import `./backend`: that module
 * reaches the barrel, and a module that throws while evaluating is one of the
 * failures being captured — importing it here would put that throw back in
 * front of the listener. What keeps the two from disagreeing about a malformed
 * `VITE_LOG_BACKEND` is not a shared import but a shared parser: both classify
 * the raw value with `parseLogBackend`, so neither can decide on its own that
 * a value the other ignores means "POST to the network".
 */
import { parseLogBackend } from "@bindery/logging/backend-choice";
import { ConsoleLogBackend } from "@bindery/logging/backends/console";
import type { LogBackend } from "@bindery/logging/backends/interface";
import { MultiLogBackend } from "@bindery/logging/backends/multi";
import { WorkerLogBackend } from "@bindery/logging/backends/worker";
import { Logger } from "@bindery/logging/logger";
import { LOG_SERVICE_NAME } from "../site";
import { getSessionId } from "./sessionId";

/** The two ways an otherwise-unhandled failure reaches this module. */
export type UnhandledErrorSource = "window.onerror" | "unhandledrejection";

/** What LoggerProvider registers to take the boundary over once it mounts. */
export type UnhandledErrorReporter = (
  error: Error,
  source: UnhandledErrorSource,
) => void;

/**
 * Cap on events emitted through the pre-mount path of one page load.
 *
 * A boot that fails in a loop — a render that throws, remounts, and throws
 * again — must not turn the boundary into an unbounded POST loop against
 * `/api/log`. Five is enough to see the failure and its immediate repeats.
 * The cap deliberately applies only while nothing has adopted the boundary:
 * once LoggerProvider is mounted the app is up, and events flow through the
 * same uncapped Logger they always have.
 */
export const MAX_EARLY_EVENTS = 5;

let reporter: UnhandledErrorReporter | null = null;
let earlyLogger: Logger | null = null;
let earlyEventCount = 0;

/**
 * Hand the boundary to a live `Logger`.
 *
 * LoggerProvider calls this on mount so events caught after the app is up
 * carry the provider's correlation context (`sessionId`; user identity stays
 * at authenticated call sites rather than in the provider). Returns the
 * disposer to hand straight back from a `useEffect`; on unmount the boundary
 * reverts to the pre-mount path below rather than going deaf.
 *
 * This is a hand-off, not a second listener: LoggerProvider must not attach
 * its own `error`/`unhandledrejection` handlers, or every failure after mount
 * would be reported twice.
 */
export function setUnhandledErrorReporter(
  next: UnhandledErrorReporter,
): () => void {
  reporter = next;
  return () => {
    if (reporter === next) reporter = null;
  };
}

/**
 * Coerce whatever a failure carried into an `Error`.
 *
 * `ErrorEvent.error` and `PromiseRejectionEvent.reason` are both untyped at
 * runtime — a rejected promise can carry a string, a number, or nothing at all
 * — and `Logger.error` needs a real message and type to build a `LogError`
 * from. Exported because every non-hook caller that catches an `unknown` and
 * wants to log it has the same problem (see src/registerServiceWorker.ts).
 */
export function toError(value: unknown, fallbackMessage: string): Error {
  if (value instanceof Error) return value;
  if (typeof value === "string") return new Error(value);
  return new Error(fallbackMessage);
}

/**
 * The backend the pre-mount path writes through.
 *
 * A raw `import.meta.env` read, classified by the same `parseLogBackend`
 * ./backend.ts uses — see this module's header for why it re-reads the
 * variable instead of importing that module, and why sharing the *parser* is
 * what keeps the two from disagreeing.
 *
 * The consequence worth stating: an unrecognized value resolves to
 * console-only here, exactly as it does there. `"axiom "` with a trailing
 * space — the shell/CI typo — is the case that makes it concrete. ./backend.ts
 * warns that it ignored the value and POSTs nothing; a boundary that resolved
 * the same value to a Worker backend would send network traffic the operator
 * was just told would not be sent.
 */
function createEarlyBackend(): LogBackend {
  switch (parseLogBackend(import.meta.env.VITE_LOG_BACKEND)) {
    case "axiom":
      return new WorkerLogBackend();
    case "both":
      return new MultiLogBackend([
        new WorkerLogBackend(),
        new ConsoleLogBackend(),
      ]);
    case "console":
      return new ConsoleLogBackend();
  }
}

/**
 * Build the pre-mount `Logger`, lazily — nothing is constructed on a page load
 * that never fails.
 *
 * It is a real `Logger` rather than a hand-rolled `fetch` so the event it
 * emits is the canonical `toAxiomEvent()` shape byte for byte
 * (logging-strategy POLICY.md §12: both ingest paths emit the identical shape,
 * and the ingest schema validates it — a hand-built event that misses a
 * required field or gets `service` wrong is dropped with a 204 and no trace).
 * What it must *not* share is the Logger LoggerProvider builds: that one comes
 * from ./backend.ts, which is on the far side of the import wall this module
 * keeps.
 *
 * `sessionId` comes from the same persisted key LoggerProvider mints from, so
 * an early event correlates with everything the visit logs afterwards.
 * `getSessionId()` never throws (it degrades to an unpersisted id), and
 * minting it here rather than skipping it costs nothing: LoggerProvider would
 * have written the same value to the same key moments later.
 */
function createEarlyLogger(): Logger {
  const version: unknown = import.meta.env.VITE_APP_VERSION;
  return new Logger(
    createEarlyBackend(),
    {
      getSessionId: () => getSessionId(),
      getUserId: () => undefined,
    },
    {
      service: LOG_SERVICE_NAME,
      environment: import.meta.env.MODE,
      version:
        typeof version === "string" && version !== "" ? version : undefined,
    },
    false,
  );
}

/**
 * Report one otherwise-unhandled failure as `infra.error.unhandled`.
 *
 * Exported so a non-React module can route a failure it caught into the same
 * boundary; the listeners below are its two built-in callers.
 *
 * Nothing here may throw. A boundary that throws while reporting replaces the
 * failure it was called about with its own, and — being called from a
 * `window.onerror` handler — would do it recursively. Every path that can
 * throw is inside the catch: `crypto.randomUUID()` is absent outside a secure
 * context, and a reporter handed in by a component can throw for reasons this
 * module cannot see.
 */
export function reportUnhandledError(
  error: Error,
  source: UnhandledErrorSource,
): void {
  try {
    if (reporter !== null) {
      reporter(error, source);
      return;
    }
    if (earlyEventCount >= MAX_EARLY_EVENTS) return;
    earlyEventCount++;
    earlyLogger ??= createEarlyLogger();
    earlyLogger.error("infra.error.unhandled", error, { source });
  } catch {
    // Deliberately silent: see above. The browser has already printed the
    // original failure to the console on its own.
  }
}

// The install itself. Guarded on `window` only so this module stays importable
// from a non-browser context (vitest's node projects, the prerender pass)
// rather than because either is expected to import it — main.tsx is the one
// entry point that does.
if (typeof window !== "undefined") {
  window.addEventListener("error", (event: ErrorEvent) => {
    reportUnhandledError(
      toError(event.error, event.message || "Unknown error"),
      "window.onerror",
    );
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportUnhandledError(
      toError(event.reason, "Unhandled promise rejection"),
      "unhandledrejection",
    );
  });
}
